// Tasks and activities live in one table and come from one list endpoint.
// The list asks for one kind by adding `kind` to its quick-filter body:
// 'TASK' for the Tasks list, 'ACTIVITY' for the Activities list. A body with
// no kind gets both, which is what the customer's Activities tab wants.
export function withKind(quickFilters, kind) {
  return { ...quickFilters, kind }
}

// Which list a type's records show on: its kind, set on the type settings screen.
// A type from before kinds existed falls back to the old name rule - a Meeting is
// an activity, every other type a task.
export function kindOfType(type) {
  if (!type) return undefined
  if (type.kind === 'TASK' || type.kind === 'ACTIVITY') return type.kind
  return type.activityTypeName === 'Meeting' ? 'ACTIVITY' : 'TASK'
}

export function typesForKind(types, kind) {
  return (types || []).filter(t => kindOfType(t) === kind)
}

// Which ActivityForm mode opens a record: the record's kind (a list row carries it),
// else its type's kind, else the name rule. An existing record opens in the form of
// its own kind, whichever screen it came from: the Activities form clears the Task
// type's start, so a dated task opened there would lose its When.
// A new record (no activityId) takes the caller's context.
// Used by the Tasks and Activities lists; the screens that show both kinds use mode
// "auto" instead (see effectiveModeFor).
export function formModeFor(activity, fallback = 'activity') {
  if (!activity || !activity.activityId) return fallback
  const kind = kindOfRecord(activity)
  if (!kind) return fallback
  return kind === 'ACTIVITY' ? 'activity' : 'task'
}

// The rules an ActivityForm applies. Mode "auto" is the form on the screens that show
// tasks and activities together: it offers every type, and its rules follow the kind of
// the type selected now - a TASK-kind type gets the task rules (a required "When", no
// duration or end), an ACTIVITY-kind type the activity rules (start, duration, end).
// With no type selected yet it uses the activity rules. Every other mode is left as is.
export function effectiveModeFor(mode, type) {
  if (mode !== 'auto') return mode
  if (!type || (!type.id && !type.activityTypeName)) return 'activity'
  return kindOfType(type) === 'TASK' ? 'task' : 'activity'
}

// The kind of a record: its own kind (a list row carries it), else its type's kind,
// else the name rule on its type name. Undefined when there is nothing to go on.
export function kindOfRecord(activity) {
  if (!activity) return undefined
  if (activity.kind === 'ACTIVITY' || activity.kind === 'TASK') return activity.kind
  const type =
    activity.activityType ||
    (activity.activityTypeName ? { activityTypeName: activity.activityTypeName } : null)
  return kindOfType(type)
}

// Which list a type's records show on, as the type settings screen offers it.
export const KIND_OPTIONS = [
  { label: 'Tasks', value: 'TASK' },
  { label: 'Events', value: 'ACTIVITY' }
]

// P7: what people call a record - "Task" or "Event" (meeting-type records were "Activities" on screen).
// A record whose kind is unknown is an Event, as the form's rules default to the event ones.
export function recordNoun(activity) {
  return kindOfRecord(activity) === 'TASK' ? 'Task' : 'Event'
}

// P7: a stored kind as a list cell shows it - "Task" or "Event" - for the Kind column on All Activities and on
// a customer's Tasks & Events tab. Empty for no kind; anything else is shown as it is.
export function kindNoun(kind) {
  if (kind === 'TASK') return 'Task'
  if (kind === 'ACTIVITY') return 'Event'
  return kind == null ? '' : kind
}

export function kindLabel(kind) {
  const option = KIND_OPTIONS.find(o => o.value === kind)
  return option ? option.label : ''
}

// P8: which records a status or result is offered for ("Shows on"). Empty means both, so nothing is hidden
// until Admin sets one. Labels follow KIND_OPTIONS.
export const SHOWS_ON_OPTIONS = [...KIND_OPTIONS, { label: 'Both', value: 'BOTH' }]

export function showsOnLabel(showsOn) {
  const option = SHOWS_ON_OPTIONS.find(o => o.value === (showsOn || 'BOTH'))
  return option ? option.label : 'Both'
}

// Whether a status or result is offered for a record of [kind] (TASK or ACTIVITY). An unknown kind sees everything.
export function fitsKind(item, kind) {
  const shows = (item && item.showsOn) || 'BOTH'
  return shows === 'BOTH' || !kind || shows === kind
}
